/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * Browser route loader - access guard matching legacy app/view/app/browser-react/browser.stateProvider.js resolve block.
 * Blocks users who lack 'documentsAccess' permission (e.g. EOS_SITE_USER when eosEnabled).
 * Also blocks INSPECTOR role when viewing SYSTEM_DOCUMENTS.
 */

import { redirect } from 'react-router'
import type { LoaderFunctionArgs } from 'react-router'

import { getUser, fetchCurrentUser } from '../services/auth'
import { accessService } from '../services/access.service'
import { initService } from '../services/init.service'
import { toast } from '../services/toast'
import i18n, { initializeI18n } from '../services/i18n/i18n'
import { UserRole } from '../types/access'
import { SystemFolderType } from '../constants/fsi'

export async function loader({ request }: LoaderFunctionArgs) {
  // Ensure user data is loaded before checking permissions (same pattern as _index.tsx)
  try {
    const cachedUser = getUser()
    if (!cachedUser) {
      await fetchCurrentUser()
    }
  } catch (error) {
    console.error('Error fetching user for browser access check:', error)
  }

  // Ensure features (EOS, ETMF, etc.) are loaded before checking access.
  // getRole() remaps USER → EOS_SITE_USER only when eosFeature.show is true,
  // and loadFeaturesInfo() must complete before that flag is available.
  // Both calls are idempotent — they return cached promises if already completed.
  await Promise.all([initService.loadFeaturesInfo(), initializeI18n()])
  // Ensure the HTTP backend has finished loading translation resources.
  // initializeI18n() may resolve before the backend completes when resources:{} is set.
  await i18n.loadNamespaces('translation')

  // Primary check: user must have documentsAccess permission
  const hasDocumentsAccess = accessService.isAllowed('documentsAccess')

  if (!hasDocumentsAccess) {
    // Pre-translate before passing to toast — matches EosSetUpStep2.tsx pattern:
    // context.service.filter.translate('KEY') then toast.error(translatedString)
    toast.error(i18n.t("Sorry, but you don't have sufficient Access Rights to access this document/content"))
    return redirect('/overview')
  }

  // Secondary check: INSPECTOR role is blocked from SYSTEM_DOCUMENTS
  // Matches legacy: if ($stateParams.type === Constants.file.systemFolderType.SYSTEM_DOCUMENTS)
  //                     accessPromises.push(AccessService.isNotAllowedAsync(accessTable.INSPECTOR))
  const url = new URL(request.url)
  const type = url.searchParams.get('type')

  if (type === SystemFolderType.SYSTEM_DOCUMENTS) {
    const isInspector = accessService.isAllowed([UserRole.INSPECTOR])
    if (isInspector) {
      toast.error(i18n.t("Sorry, but you don't have sufficient Access Rights to access this document/content"))
      return redirect('/overview')
    }
  }

  return null
}
